import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Box,
    Chip,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import { ExperienceType } from '../../enums/experience';

export type PolicyTypeFilter =
    | 'All'
    | 'Flexible'
    | 'Moderate'
    | 'Strict'
    | 'Fair'
    | 'Custom';
export type PolicySortKey =
    | 'nameAsc'
    | 'nameDesc'
    | 'createdDesc'
    | 'createdAsc';

const ALL_EXPERIENCES: ExperienceType[] = Object.values(
    ExperienceType,
) as ExperienceType[];

export default function PolicyToolbar({
    query,
    onQueryChange,
    typeFilter,
    onTypeFilterChange,
    expFilter,
    onExpFilterChange,
    sortKey,
    onSortKeyChange,
}: {
    query: string;
    onQueryChange: (v: string) => void;
    typeFilter: PolicyTypeFilter;
    onTypeFilterChange: (v: PolicyTypeFilter) => void;
    expFilter: ExperienceType[];
    onExpFilterChange: (v: ExperienceType[]) => void;
    sortKey: PolicySortKey;
    onSortKeyChange: (v: PolicySortKey) => void;
}) {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const handleAddClick = () => {
        navigate('/newPolicy');
    };

    return (
        <Stack
            sx={{
                py: isMobile || isIpad ? 1 : 2,
                width: '100%',
            }}
        >
            <Grid container spacing={1.5} alignItems='center'>
                <Grid item xs={12} md>
                    {/* Helper text */}
                    <Typography sx={{ fontSize: '12px' }}>
                        Create and manage custom policies to set clear terms for
                        your experiences.
                    </Typography>
                </Grid>

                <Grid item xs={12} md='auto' sx={{ textAlign: 'right' }}>
                    {isMobile || isIpad ? (
                        <CustomIconButton
                            variant='contained'
                            color='primary'
                            onClick={handleAddClick}
                        >
                            <AddCircleIcon />
                        </CustomIconButton>
                    ) : (
                        <CustomButton
                            id='addPolicyType'
                            variant='contained'
                            color='primary'
                            startIcon={<AddCircleIcon />}
                            onClick={handleAddClick}
                        >
                            Add New
                        </CustomButton>
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ paddingTop: '30px' }}>
                <Grid item xs={12} md={4}>
                    {/* Search (flex grows to fill remaining space) */}
                    <TextField
                        fullWidth
                        placeholder='Search policies…'
                        size='small'
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            ),
                        }}
                    />{' '}
                </Grid>
                <Grid item xs={12} md={2.5}>
                    {/* Policy Type (fixed width) */}
                    <Select
                        fullWidth
                        size='small'
                        value={typeFilter}
                        onChange={(e) =>
                            onTypeFilterChange(
                                e.target.value as PolicyTypeFilter,
                            )
                        }
                    >
                        <MenuItem value='All'>All Types</MenuItem>
                        <MenuItem value='Flexible'>Flexible</MenuItem>
                        <MenuItem value='Moderate'>Moderate</MenuItem>
                        <MenuItem value='Strict'>Strict</MenuItem>
                        <MenuItem value='Fair'>Fair</MenuItem>
                        <MenuItem value='Custom'>Custom</MenuItem>
                    </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                    {/* Experience Types (fixed width) */}
                    <Autocomplete
                        multiple
                        size='small'
                        options={ALL_EXPERIENCES}
                        value={expFilter}
                        onChange={(_, v) => onExpFilterChange(v)}
                        getOptionLabel={(option) =>
                            option.charAt(0).toUpperCase() + option.slice(1)
                        }
                        renderInput={(params) => (
                            <TextField
                                sx={{
                                    '& .MuiInputBase-root': { borderRadius: 2 },
                                }}
                                {...params}
                                label='Experience types'
                                placeholder='All'
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    size='small'
                                    label={
                                        option.charAt(0).toUpperCase() +
                                        option.slice(1)
                                    }
                                />
                            ))
                        }
                    />
                </Grid>
                <Grid item xs={12} md={1.5}>
                    {/* Sort (fixed width) */}
                    <Select
                        fullWidth
                        size='small'
                        value={sortKey}
                        onChange={(e) =>
                            onSortKeyChange(e.target.value as PolicySortKey)
                        }
                    >
                        <MenuItem value='nameAsc'>Sort: Name ↑</MenuItem>
                        <MenuItem value='nameDesc'>Sort: Name ↓</MenuItem>
                        <MenuItem value='createdDesc'>Sort: Newest</MenuItem>
                        <MenuItem value='createdAsc'>Sort: Oldest</MenuItem>
                    </Select>
                </Grid>
            </Grid>
        </Stack>
    );
}
